import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

// Types mirror the backend appointments module resources
// (adaptivex-cc-backend/src/modules/appointments/http/resources).

export type AppointmentStatus = "booked" | "completed" | "cancelled" | "no_show";
export type AppointmentSource = "ai" | "manual";

export interface BookableService {
	id: string;
	name: string;
	durationMinutes: number;
	price: number | null;
	recallIntervalDays: number | null;
	enabled: boolean;
	sortOrder: number;
	createdAt: string;
}

export interface BookableServicePayload {
	name: string;
	durationMinutes: number;
	price?: number | null;
	recallIntervalDays?: number | null;
	enabled?: boolean;
	sortOrder?: number;
}

export interface AppointmentContact {
	id: string;
	phone: string;
	attributes?: Record<string, any>;
}

export interface Appointment {
	syncState?: "synced" | "pending_create" | "pending_move" | "pending_cancel";
	externalId?: string | null;
	staffId?: string | null;
	id: string;
	serviceId: string;
	contactId: string;
	startAt: string;
	endAt: string;
	status: AppointmentStatus;
	source: AppointmentSource;
	callId: string | null;
	notes: string | null;
	createdAt: string;
	service?: BookableService;
	contact?: AppointmentContact;
}

export interface AppointmentsResponse {
	data: Appointment[];
	total: number;
}

export interface AppointmentsFilter {
	allPages?: boolean;
	from?: string;
	to?: string;
	contactId?: string;
	serviceId?: string;
	status?: AppointmentStatus;
	limit?: number;
	offset?: number;
}

export interface BusinessHoursEntry {
	id?: string;
	// 1-7 (Monday-Sunday)
	dow: number;
	// "HH:MM"
	from: string;
	to: string;
}

export interface ScheduleException {
	id: string;
	date: string;
	closed: boolean;
	from: string | null;
	to: string | null;
	note: string | null;
}

export interface AvailabilitySlot {
	start: string;
	end: string;
	serviceId: string;
}

export const appointmentsKeys = {
	all: ["appointments"] as const,
	lists: () => [...appointmentsKeys.all, "list"] as const,
	list: (filter: AppointmentsFilter) => [...appointmentsKeys.lists(), filter] as const,
	details: () => [...appointmentsKeys.all, "detail"] as const,
	detail: (id: string) => [...appointmentsKeys.details(), id] as const,
	services: () => [...appointmentsKeys.all, "services"] as const,
	businessHours: () => [...appointmentsKeys.all, "business-hours"] as const,
	exceptions: () => [...appointmentsKeys.all, "exceptions"] as const,
	availability: (params: AvailabilityParams) => [...appointmentsKeys.all, "availability", params] as const,
};

function buildAppointmentsQuery(filter: AppointmentsFilter): string {
	const params = new URLSearchParams();
	if (filter.from) params.set("from", filter.from);
	if (filter.to) params.set("to", filter.to);
	if (filter.contactId) params.set("contactId", filter.contactId);
	if (filter.serviceId) params.set("serviceId", filter.serviceId);
	if (filter.status) params.set("status", filter.status);
	params.set("limit", String(filter.limit ?? 100));
	params.set("offset", String(filter.offset ?? 0));
	return params.toString();
}

export function errorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message;
	return Array.isArray(message) ? message[0] : message || fallback;
}

// GET /appointments?from=&to=&contactId=&serviceId=&status= (paginated)
export function useAppointments(filter: AppointmentsFilter, enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: appointmentsKeys.list(filter),
		queryFn: async (): Promise<AppointmentsResponse> => {
			const response = await apiClient.get<AppointmentsResponse>(`/appointments?${buildAppointmentsQuery(filter)}`);
			if (!filter.allPages) return response.data;
			const result = { ...response.data, data: [...response.data.data] };
			// `total` counts the whole range; the caller's own offset is already behind us.
			const skipped = filter.offset ?? 0;
			while (result.data.length < result.total - skipped) {
				const next = await apiClient.get<AppointmentsResponse>(
					`/appointments?${buildAppointmentsQuery({ ...filter, offset: skipped + result.data.length })}`
				);
				if (next.data.data.length === 0)
					throw new Error("The calendar changed while loading. Refresh to see the full range.");
				result.data.push(...next.data.data);
				result.total = next.data.total;
			}
			return result;
		},
		enabled,
		retry: false,
	});
}

// GET /appointments/services
export function useBookableServices(enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: appointmentsKeys.services(),
		queryFn: async (): Promise<BookableService[]> => {
			const response = await apiClient.get("/appointments/services");
			return response.data;
		},
		enabled,
		retry: false,
	});
}

// POST /appointments/services
export function useCreateBookableService() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (data: BookableServicePayload): Promise<BookableService> => {
			const response = await apiClient.post("/appointments/services", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.services() });
			toast.success("Service added");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't add the service — please try again"));
		},
	});
}

// PUT /appointments/services/:id
export function useUpdateBookableService() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async ({ id, data }: { id: string; data: BookableServicePayload }): Promise<BookableService> => {
			const response = await apiClient.put(`/appointments/services/${id}`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.services() });
			toast.success("Service updated");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't update the service — please try again"));
		},
	});
}

// DELETE /appointments/services/:id
export function useDeleteBookableService() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/appointments/services/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.services() });
			toast.success("Service removed");
		},
		onError: (error: any) => {
			logger.error(error);
			// Backend returns 400 when the service has appointments.
			toast.error(errorMessage(error, "We couldn't remove the service — please try again"));
		},
	});
}

// GET /appointments/business-hours
export function useBusinessHours(enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: appointmentsKeys.businessHours(),
		queryFn: async (): Promise<BusinessHoursEntry[]> => {
			const response = await apiClient.get("/appointments/business-hours");
			return response.data;
		},
		enabled,
		retry: false,
	});
}

// PUT /appointments/business-hours {hours: [{dow, from, to}]}
export function usePutBusinessHours() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (
			hours: Array<Pick<BusinessHoursEntry, "dow" | "from" | "to">>
		): Promise<BusinessHoursEntry[]> => {
			const response = await apiClient.put("/appointments/business-hours", { hours });
			return response.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(appointmentsKeys.businessHours(), data);
			toast.success("Opening hours saved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't save your hours — please try again"));
		},
	});
}

// GET /appointments/exceptions
export function useScheduleExceptions(enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: appointmentsKeys.exceptions(),
		queryFn: async (): Promise<ScheduleException[]> => {
			const response = await apiClient.get("/appointments/exceptions");
			return response.data;
		},
		enabled,
		retry: false,
	});
}

// POST /appointments/exceptions
export function useCreateScheduleException() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (data: {
			date: string;
			closed?: boolean;
			from?: string | null;
			to?: string | null;
			note?: string | null;
		}): Promise<ScheduleException> => {
			const response = await apiClient.post("/appointments/exceptions", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't save the closure — please try again"));
		},
	});
}

// DELETE /appointments/exceptions/:id
export function useDeleteScheduleException() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/appointments/exceptions/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't remove the closure — please try again"));
		},
	});
}

export interface AvailabilityParams {
	staffId?: string;
	serviceId: string;
	from: string;
	to: string;
	timezone?: string;
}

// GET /appointments/availability?serviceId=&from=&to=&timezone=
export function useAvailability(params: AvailabilityParams, enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: appointmentsKeys.availability(params),
		queryFn: async (): Promise<AvailabilitySlot[]> => {
			const searchParams = new URLSearchParams({
				serviceId: params.serviceId,
				from: params.from,
				to: params.to,
			});
			if (params.staffId) searchParams.set("staffId", params.staffId);
			if (params.timezone) searchParams.set("timezone", params.timezone);
			const response = await apiClient.get(`/appointments/availability?${searchParams.toString()}`);
			return response.data;
		},
		enabled: enabled && Boolean(params.serviceId),
		retry: false,
	});
}

// POST /appointments {serviceId, contactId | phone, startAt, notes?}
export function useCreateAppointment() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (data: {
			serviceId: string;
			staffId?: string;
			contactId?: string;
			phone?: string;
			startAt: string;
			notes?: string | null;
			/** The zone the opening hours are read in; the same one availability was asked for. */
			timezone?: string;
		}): Promise<Appointment> => {
			const response = await apiClient.post("/appointments", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
			toast.success("Appointment booked");
		},
		onError: (error: any) => {
			logger.error(error);
			void queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
			toast.error(errorMessage(error, "We couldn't book that time — please try another slot"));
		},
	});
}

// PATCH /appointments/:id/status {status}
export function useUpdateAppointmentStatus() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async ({ id, status }: { id: string; status: AppointmentStatus }): Promise<Appointment> => {
			const response = await apiClient.patch(`/appointments/${id}/status`, { status });
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't update the appointment — please try again"));
		},
	});
}

// POST /appointments/:id/reschedule {startAt}
export function useRescheduleAppointment() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async ({
			id,
			startAt,
			timezone,
		}: {
			id: string;
			startAt: string;
			timezone?: string;
		}): Promise<Appointment> => {
			const response = await apiClient.post(`/appointments/${id}/reschedule`, { startAt, timezone });
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
			toast.success("Appointment rescheduled");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't reschedule — please try another time"));
		},
	});
}

// DELETE /appointments/:id — cancels the appointment.
export function useCancelAppointment() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/appointments/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: appointmentsKeys.all });
			toast.success("Appointment cancelled");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't cancel the appointment — please try again"));
		},
	});
}
